import { adminApiClient } from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { getApiClientHeaderForMode } from '@/utils/apiConfig'

type ModeAwareRequestConfig = {
  headers: Record<string, string>
  __preserveAppClientHeader: boolean
}

type ApiRecord = Record<string, unknown>

const buildModeAwareConfig = (mode: NetworkEnvironment): ModeAwareRequestConfig => ({
  headers: {
    'X-App-Client': getApiClientHeaderForMode(mode),
  },
  __preserveAppClientHeader: true,
})

const pick = (record: ApiRecord, camelKey: string, snakeKey: string): unknown =>
  record[camelKey] ?? record[snakeKey]

const toStringValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  return String(value)
}

const toStringOrNull = (value: unknown): string | null => {
  const stringValue = toStringValue(value)
  return stringValue === undefined ? null : stringValue
}

const toNumberValue = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toNumberOrNull = (value: unknown): number | null =>
  toNumberValue(value) ?? null

const toBooleanOrNull = (value: unknown): boolean | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.toLowerCase()
    if (normalized === 'true' || normalized === '1') return true
    if (normalized === 'false' || normalized === '0') return false
  }
  return null
}

export type CooldownOperationTypeRow = {
  id: string
  operation_type: string
  display_name: string
  default_duration_seconds: number
  description: string | null
  storage_strategy: 'kv' | 'd1' | string
  max_duration_seconds: number | null
  min_duration_seconds: number | null
  requires_audit: number | boolean | null
  admin_override_allowed: number | boolean | null
  is_testnet: number | boolean
  created_at?: string | null
  updated_at?: string | null
}

export type CooldownOperationTypesResponse = {
  status: string
  data: {
    dbTypes: CooldownOperationTypeRow[]
    runtimeConfigs: unknown[]
    mode: NetworkEnvironment
    timestamp: string
  }
}

export type CooldownOperationTypeUpdatePayload = {
  displayName?: string
  defaultDurationSeconds?: number
  description?: string | null
  maxDurationSeconds?: number | null
  minDurationSeconds?: number | null
  adminOverrideAllowed?: boolean
}

export type CooldownRecordRow = {
  id: string | null
  operation_type_id: string
  operation_type: string
  operation_display_name?: string | null
  operation_type_join_status?: 'ok' | 'missing_operation_type' | 'operation_type_mode_mismatch'
  storage_strategy?: 'kv' | 'd1' | string
  identifier: string
  user_email?: string | null
  context: string | null
  expires_at: string
  duration_seconds: number
  status: string
  effective_status?: string
  created_at?: string | null
  updated_at?: string | null
  created_by?: string | null
  reason?: string | null
  overridden_by?: string | null
  overridden_at?: string | null
  override_reason?: string | null
  source?: 'd1' | 'kv' | string
  key?: string
  admin_override_allowed?: number | boolean | null
}

export type CooldownRecordsResponse = {
  status: string
  data: {
    records: CooldownRecordRow[]
    kvRecords: CooldownRecordRow[]
    total: number
    page: number
    totalPages: number
    filters: Record<string, unknown>
    mode: NetworkEnvironment
    recordsTable: string
  }
}

export type CooldownStatsResponse = {
  status: string
  data: {
    overview?: {
      totalRecords?: number
      activeRecords?: number
      expiredRecords?: number
      overriddenRecords?: number
      timeWindow?: number
    }
    kvActiveRecords?: number
    mode: NetworkEnvironment
    recordsTable: string
    timestamp: string
  }
}

export type CooldownOverridePayload = {
  recordId?: string
  operationType?: string
  identifier?: string
  context?: string | null
  reason: string
}

const normalizeOperationTypeRow = (row: ApiRecord): CooldownOperationTypeRow => ({
  id: toStringValue(row.id) ?? '',
  operation_type: toStringValue(pick(row, 'operationType', 'operation_type')) ?? '',
  display_name: toStringValue(pick(row, 'displayName', 'display_name')) ?? '',
  default_duration_seconds:
    toNumberValue(pick(row, 'defaultDurationSeconds', 'default_duration_seconds')) ?? 0,
  description: toStringOrNull(row.description),
  storage_strategy: toStringValue(pick(row, 'storageStrategy', 'storage_strategy')) ?? '',
  max_duration_seconds: toNumberOrNull(pick(row, 'maxDurationSeconds', 'max_duration_seconds')),
  min_duration_seconds: toNumberOrNull(pick(row, 'minDurationSeconds', 'min_duration_seconds')),
  requires_audit: toBooleanOrNull(pick(row, 'requiresAudit', 'requires_audit')),
  admin_override_allowed: toBooleanOrNull(
    pick(row, 'adminOverrideAllowed', 'admin_override_allowed')
  ),
  is_testnet: toBooleanOrNull(pick(row, 'isTestnet', 'is_testnet')) ?? false,
  created_at: toStringOrNull(pick(row, 'createdAt', 'created_at')),
  updated_at: toStringOrNull(pick(row, 'updatedAt', 'updated_at')),
})

const normalizeRecordRow = (row: ApiRecord): CooldownRecordRow => ({
  id: toStringOrNull(row.id),
  operation_type_id: toStringValue(pick(row, 'operationTypeId', 'operation_type_id')) ?? '',
  operation_type: toStringValue(pick(row, 'operationType', 'operation_type')) ?? '',
  operation_display_name: toStringOrNull(
    pick(row, 'operationDisplayName', 'operation_display_name')
  ),
  operation_type_join_status: toStringValue(
    pick(row, 'operationTypeJoinStatus', 'operation_type_join_status')
  ) as CooldownRecordRow['operation_type_join_status'],
  storage_strategy: toStringValue(
    pick(row, 'storageStrategy', 'storage_strategy')
  ) as CooldownRecordRow['storage_strategy'],
  identifier: toStringValue(row.identifier) ?? '',
  user_email: toStringOrNull(pick(row, 'userEmail', 'user_email')),
  context: toStringOrNull(row.context),
  expires_at: toStringValue(pick(row, 'expiresAt', 'expires_at')) ?? '',
  duration_seconds: toNumberValue(pick(row, 'durationSeconds', 'duration_seconds')) ?? 0,
  status: toStringValue(row.status) ?? '',
  effective_status: toStringValue(pick(row, 'effectiveStatus', 'effective_status')),
  created_at: toStringOrNull(pick(row, 'createdAt', 'created_at')),
  updated_at: toStringOrNull(pick(row, 'updatedAt', 'updated_at')),
  created_by: toStringOrNull(pick(row, 'createdBy', 'created_by')),
  reason: toStringOrNull(row.reason),
  overridden_by: toStringOrNull(pick(row, 'overriddenBy', 'overridden_by')),
  overridden_at: toStringOrNull(pick(row, 'overriddenAt', 'overridden_at')),
  override_reason: toStringOrNull(pick(row, 'overrideReason', 'override_reason')),
  source: toStringValue(row.source) as CooldownRecordRow['source'],
  key: toStringValue(row.key),
  admin_override_allowed: toBooleanOrNull(
    pick(row, 'adminOverrideAllowed', 'admin_override_allowed')
  ),
})

export const cooldownService = {
  async getOperationTypes(mode: NetworkEnvironment): Promise<CooldownOperationTypesResponse['data']> {
    const response = await adminApiClient.get<CooldownOperationTypesResponse>(
      '/admin/cooldown/types',
      buildModeAwareConfig(mode)
    )
    return {
      ...response.data.data,
      dbTypes: (response.data.data.dbTypes ?? []).map(row =>
        normalizeOperationTypeRow(row as unknown as ApiRecord)
      ),
    }
  },

  async updateOperationType(
    mode: NetworkEnvironment,
    operationType: string,
    payload: CooldownOperationTypeUpdatePayload
  ): Promise<CooldownOperationTypeRow> {
    const response = await adminApiClient.patch<{
      status: string
      data: { operationType: CooldownOperationTypeRow }
    }>(
      `/admin/cooldown/types/${encodeURIComponent(operationType)}`,
      payload,
      buildModeAwareConfig(mode)
    )
    return normalizeOperationTypeRow(
      response.data.data.operationType as unknown as ApiRecord
    )
  },

  async getRecords(
    mode: NetworkEnvironment,
    params: {
      page?: number
      limit?: number
      operationType?: string
      identifier?: string
      status?: string
    }
  ): Promise<CooldownRecordsResponse['data']> {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).length > 0) {
        search.set(key, String(value))
      }
    })
    const response = await adminApiClient.get<CooldownRecordsResponse>(
      `/admin/cooldown/records?${search.toString()}`,
      buildModeAwareConfig(mode)
    )
    return {
      ...response.data.data,
      records: (response.data.data.records ?? []).map(row =>
        normalizeRecordRow(row as unknown as ApiRecord)
      ),
      kvRecords: (response.data.data.kvRecords ?? []).map(row =>
        normalizeRecordRow(row as unknown as ApiRecord)
      ),
    }
  },

  async getStats(
    mode: NetworkEnvironment,
    params: {
      operationType?: string
      hours?: number
    }
  ): Promise<CooldownStatsResponse['data']> {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).length > 0) {
        search.set(key, String(value))
      }
    })
    const response = await adminApiClient.get<CooldownStatsResponse>(
      `/admin/cooldown/stats?${search.toString()}`,
      buildModeAwareConfig(mode)
    )
    return response.data.data
  },

  async override(
    mode: NetworkEnvironment,
    payload: CooldownOverridePayload
  ): Promise<Record<string, unknown>> {
    const response = await adminApiClient.post<{
      status: string
      data: Record<string, unknown>
    }>(
      '/admin/cooldown/override',
      payload,
      buildModeAwareConfig(mode)
    )
    return response.data.data
  },

  async overrideRecord(
    mode: NetworkEnvironment,
    recordId: string,
    payload: Omit<CooldownOverridePayload, 'recordId'>
  ): Promise<Record<string, unknown>> {
    const response = await adminApiClient.post<{
      status: string
      data: Record<string, unknown>
    }>(
      `/admin/cooldown/records/${encodeURIComponent(recordId)}/override`,
      payload,
      buildModeAwareConfig(mode)
    )
    return response.data.data
  },
}
